import React, {
    ChangeEvent,
    FunctionComponent,
    KeyboardEvent,
    useEffect,
    useRef,
    useState,
} from 'react';

import { useUnit } from 'effector-react';

import clsx from 'clsx';
import { useRouter } from 'next/router';
import { RemoveScroll } from 'react-remove-scroll';
import { Tooltip } from 'react-tooltip';

import { Product } from '@domains/product';

import { productsAutocompleteFx } from '@stores/products-list';

import { sendUseSearchEvent } from '@utils/analytics';
import { defaultSearchLimit } from '@utils/products-list/constants';
import { makeProductListQuery } from '@utils/products-list/query';
import { makeSearchRoute } from '@utils/route';
import { SnackBar } from '@utils/snackbar';
import { applyTypograf } from '@utils/typograf';
import { pluralize } from '@utils/utils';

import { useClickOutside } from '@hooks/click-outside';
import { useDebounce } from '@hooks/use-debounce';

import { Button } from '@components/common/Button/Button';
import { Spinner } from '@components/common/Spinner/Spinner';
import { SearchIcon } from '@components/icons';
import { ProductCard } from '@components/product/ProductCard/ProductCard';

import styles from './SearchWidget.module.scss';

export const SearchWidget: FunctionComponent = () => {
    const [query, setQuery] = useState('');
    const [isShowResults, setShowResults] = useState(false);
    const [items, setItems] = useState<Product[]>([]);
    const [isTooltipShown, setTooltipShown] = useState(false);

    const productsAutocomplete = useUnit(productsAutocompleteFx);
    const isProductsAutocompletePending = useUnit(productsAutocompleteFx.pending);

    const { push } = useRouter();

    const [debouncedQuery] = useDebounce(query, 500);

    const wrapperRef = useRef<HTMLDivElement>(null);

    useClickOutside({
        ref: wrapperRef,
        action: () => setShowResults(false),
    });

    const submitSearch = () => {
        setShowResults(false);

        push({
            pathname: makeSearchRoute(),
            query: makeProductListQuery({ searchQuery: query }),
        });
    };

    const handleSearchIconButtonClick = () => {
        if (query.length < 3) return;
        submitSearch();
    };

    const onTextChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (value.length >= 3) {
            setTooltipShown(false);
        } else {
            setShowResults(false);
            setTooltipShown(true);
        }
        setQuery(value);
    };

    const onFocus = () => {
        if (query.length >= 3) {
            setShowResults(true);
        } else {
            setTooltipShown(true);
        }
    };

    const onBlur = () => {
        setTooltipShown(false);
    };

    const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && query.length >= 3) submitSearch();
    };

    const onShowMoreClick = () => {
        submitSearch();
    };

    useEffect(() => {
        if (debouncedQuery.length >= 3) {
            setShowResults(true);
            productsAutocomplete({
                limit: defaultSearchLimit,
                search_query: debouncedQuery,
            })
                .then((result) => {
                    setItems(result.items);
                    sendUseSearchEvent();
                })
                .catch(SnackBar.handleApiError);
        }
    }, [debouncedQuery, productsAutocomplete]);

    const tooltipCounter = 3 - query.length;

    return (
        <div className={styles.root} ref={wrapperRef}>
            <div className={styles.search} id="search-widget-tooltip">
                <input
                    value={query}
                    onChange={onTextChange}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    onKeyDown={onKeyDown}
                    type="text"
                    placeholder="Введите название товара или артикул"
                    className={styles.input}
                    data-testid="search-input"
                />
                <button
                    type="button"
                    className={styles.button}
                    onClick={handleSearchIconButtonClick}
                >
                    <SearchIcon className={styles.icon} />
                </button>
            </div>

            <Tooltip
                anchorSelect="#search-widget-tooltip"
                place="bottom-start"
                classNameArrow={styles.tooltipArrow}
                isOpen={isTooltipShown}
            >
                Введите {tooltipCounter > 2 ? tooltipCounter : `еще ${tooltipCounter}`}{' '}
                {pluralize(tooltipCounter, ['символ', 'символа', 'символов'])}
            </Tooltip>

            <RemoveScroll
                enabled={isShowResults}
                noIsolation
                className={clsx(styles.results, !isShowResults && styles.isHidden)}
            >
                {isProductsAutocompletePending && <Spinner classes={{ root: styles.spinner }} />}
                {!isProductsAutocompletePending && items.length > 0 && (
                    <>
                        {items.map((item) => (
                            <ProductCard
                                key={item.slug}
                                product={item}
                                isHorizontal
                                className={styles.productItem}
                                onClick={() => setShowResults(false)}
                            />
                        ))}
                        <div className={styles.buttonWrapper}>
                            <Button
                                variant="greenLight"
                                isFullWith
                                className={styles.showMoreButton}
                                onClick={onShowMoreClick}
                            >
                                Показать еще
                            </Button>
                        </div>
                    </>
                )}

                {!isProductsAutocompletePending && items.length === 0 && (
                    <span className={styles.emptyState}>
                        {applyTypograf('По вашему запросу ничего не найдено')}
                    </span>
                )}
            </RemoveScroll>
        </div>
    );
};
